import React from 'react'
import { PageSection as Section, Button, Grid, getPictureProps } from '@superhi/design'

import CourseProjectCard from './components/CourseProjectCard'
import { Wrapper, BackgroundImage } from './styles'

import BackgroundImageWarped from 'src/components/BackgroundImageWarped'
import { buildPath } from 'src/routing'

const COURSES_PATH = buildPath('courses-index')

const GRID_1_SLIDER = { small: true }
const GRID_1_CELL_SPAN_1: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 3,
  medium: 4,
  large: 4,
}

const backgroundImageProps = getPictureProps({
  small: {
    path: 'https://superhi.s3.amazonaws.com/assets/images/unlimited-membership/projects-background-small.png',
    width: 100,
    options: {
      fm: 'jpg',
    },
  },
  medium: {
    path: 'https://superhi.s3.amazonaws.com/assets/images/unlimited-membership/projects-background-medium.png',
    width: 100,
    options: {
      fm: 'jpg',
    },
  },
  large: {
    path: 'https://superhi.s3.amazonaws.com/assets/images/unlimited-membership/projects-background-large.png',
    width: 100,
    options: {
      fm: 'jpg',
    },
  },
  xlarge: {
    path: 'https://superhi.s3.amazonaws.com/assets/images/unlimited-membership/projects-background-large.png',
    width: 100,
    options: {
      fm: 'jpg',
    },
  },
})

const DATA: React.ComponentProps<typeof CourseProjectCard>[] = [
  {
    title: 'Take your shaders to the next dimension',
    description:
      'Use Three.js to make a 3D metal jellyfish with fully-custom lighting and movements.',
    imageUrl:
      'https://superhi.s3.amazonaws.com/assets/images/membership-projects/Shaders-for-the-Web-08.png',
    imageAlt:
      'A 3D purple object with a metal finish; an interlinking chain-like, curved silhouette; and highlights on certain areas.',
    courseTitle: 'Shaders for the Web',
    courseSlug: 'shaders-for-the-web',
  },
  {
    title: 'Create eye-popping visual effects',
    description:
      'Have fun experimenting with alpha textures and an extracted face texture before hooking them all up to the Patch Editor to create a playful eye-popping filter!',
    imageUrl:
      'https://superhi.s3.amazonaws.com/assets/images/membership-projects/Instagram-Filters-03.png',
    imageAlt:
      'An image of a person with their eyes duplicated and popping out of their head, set on an electric blue background.',
    courseTitle: 'Instagram Filters + Spark AR',
    courseSlug: 'how-to-make-instagram-filters-with-spark-ar',
  },
  {
    title: 'Bring a photography site to life with animation',
    description:
      "From sticky scrolling to wipe and fade effects, you'll learn how to amp up a photo studio site with cool animations.",
    imageUrl:
      'https://superhi.s3.amazonaws.com/assets/images/membership-projects/Animation-for-the-Web-05.png',
    imageAlt:
      "A portrait of a person with the words 'Seal Street Photo Studio' overlaid in white, set on a blue and green ombré background.",
    courseTitle: 'Animation for the Web',
    courseSlug: 'animation-for-the-web-svg-css-and-javascript',
  },
  {
    title: 'Take kinetic type to the max',
    description:
      'Get experimental with typography by letting users change the text as well as the speed of animation.',
    imageUrl:
      'https://superhi.s3.amazonaws.com/assets/images/membership-projects/Experimental-Typography-05.png',
    imageAlt:
      "The words 'Good Morning' written in large red typography that has jagged edges, set on a pale dusty pink background.",
    courseTitle: 'Experimental Typography for the Web',
    courseSlug: 'experimental-typography-for-the-web',
  },
  {
    title: 'Add 3d models to websites using Javascript',
    description:
      'Upload your own 3d models to the web and let your users interact with them using Three.js!',
    imageUrl:
      'https://superhi.s3.amazonaws.com/assets/images/membership-projects/Experimental-Javascript-06.png',
    imageAlt: 'A 3D model of a cat with an alarming expression, sitting on a grey rounded surface.',
    courseTitle: 'Experimental Javascript',
    courseSlug: 'experimental-javascript',
  },
  {
    title: 'Build an emoji land in Python',
    description:
      'Create a procedural generated landscape, as seen in Roblox, Minecraft, and on several popular Twitter accounts, using the toolsets included in Python.',
    imageUrl:
      'https://superhi.s3.amazonaws.com/assets/images/membership-projects/Intro-to-Python-01.png',
    imageAlt:
      "A mosaic made up of hundreds of emojis to form a bird's-eye view of a fictional land. Blue represents sea and green is land.",
    courseTitle: 'Intro to Python',
    courseSlug: 'intro-to-python',
  },
]

const BACKGROUND_IMAGE_OPTIONS: React.ComponentProps<typeof BackgroundImageWarped>['options'] = {
  small: {
    ar: '50:100',
    fit: 'crop',
    crop: 'bottom',
  },
  medium: {
    ar: '50:100',
    fit: 'crop',
    crop: 'bottom',
  },
  large: {
    ar: '100:100',
    fit: 'crop',
    crop: 'bottom',
  },
  xlarge: {
    ar: '125:100',
    fit: 'crop',
    crop: 'bottom',
  },
}

const Projects = () => (
  <BackgroundImageWarped color="blue" position="top center" options={BACKGROUND_IMAGE_OPTIONS}>
    <Section
      title="Cool things you can learn"
      subtitle="There are 125+ creative projects to choose from! Take a full course or get experimental by dipping into individual projects."
      footer={<Button href={COURSES_PATH}>Browse our courses for more projects</Button>}
      headingLevel={2}
    >
      <Grid alignItems="stretch" slider={GRID_1_SLIDER}>
        {DATA.map((item, i) => (
          <Grid.Cell key={i} span={GRID_1_CELL_SPAN_1}>
            <CourseProjectCard {...item} />
          </Grid.Cell>
        ))}
      </Grid>
    </Section>
  </BackgroundImageWarped>
)

export default Projects
